import { useIntl } from 'react-intl'
import { useMemo, useState } from 'react'
import { DataTable, TableSkeletonLoader, Modal } from "@/components/atoms"
import { SIZE } from '@/utils/constants/globalStyle'
import { SELECTION_TYPE } from '@/utils/constants/commonConstants'
import { RadioButton } from '@/containers/ClubPlus/Tabs/TierTab/RadioButton'
import { postMoveStores } from '@/services/ClubPlus/TierService'
import { useAppDispatch } from '@/store'
import { setToastNotifications } from '@/store/slices/commonSlice'
import { useActiveTiers } from '@/containers/ClubPlus/hooks/useActiveTiers'

export const MoveStoresModal = ({ isOpen, closeModal, selectedStores, tierName, tierID }) => {
  const { tiers, isLoadingTiers } = useActiveTiers()
  const [selectedTierID, setSelectedTierID] = useState(() => tierID)
  const { formatMessage } = useIntl()
  const dispatch = useAppDispatch()

  const handleMoveStores = async () => {
    const params = {
      newTierID: selectedTierID,
      storeIds: selectedStores
    }

    try {
      let response = await postMoveStores(selectedTierID, params);

      if (response?.success) {
        dispatch(setToastNotifications({ show: true, message: formatMessage({ id: 'CLUB_PLUS.' }), type: 'success' }))
        closeModal()
      } else {
        const errMsg = response?.errorMessage || formatMessage({ id: 'GENERAL.UNKNOWN_ERROR' })
        dispatch(setToastNotifications({ show: true, message: errMsg, type: 'error' }))
        closeModal()
      }
    } catch (error) {
      const errMsg = error?.response?.data?.errorMessage || error?.message || 'An unknown error occurred'
      dispatch(setToastNotifications({ show: true, message: errMsg, type: 'error' }))
      closeModal()
    }
  }

  const tiersList = useMemo(() => {
    if (tiers === undefined || tiers?.length < 1 || isLoadingTiers) return []

    return tiers.map(tier => ({
      tierName: `${tier?.name}${tier?.tierID === tierID ? formatMessage({ id: 'CLUB_PLUS.TIERS_MOVE_STORES_CURRENT_TIER' }) : ''}`,
      storesAssociated: tier?.storesAssociated ?? 0,
      tierID: tier?.tierID,
      isSelected: tier?.tierID === selectedTierID
    }))
  }, [tiers, tierID, formatMessage, isLoadingTiers, selectedTierID])


  const columns = useMemo(() => [
    {
      columnKey: 'tierName', header: formatMessage({ id: 'CLUB_PLUS.TIER_LIST_TABLE_COLUMN_NAME' }), cellData: ctx => {
        const isSelected = ctx?.row?.original?.isSelected;
        const currentTierID = ctx?.row?.original?.tierID;
        const tierName = ctx?.row?.original?.tierName;

        return (
          <div className="table-row">
            <RadioButton
              isSelected={isSelected}
              onChange={() => setSelectedTierID(currentTierID)}
            />
            <span>{tierName}</span>
          </div>
        )
      }
    },
    { columnKey: 'storesAssociated', header: formatMessage({ id: 'CLUB_PLUS.TIERS_MOVE_STORES_NUMBER_COLUMN' }) },
  ], [formatMessage])

  return (
    <Modal
      showModal={isOpen}
      isDark
      onCloseModal={closeModal}
      primaryButtonText={formatMessage({ id: 'GENERAL.MOVE' })}
      primaryButtonDisabled={selectedTierID === tierID}
      secondaryButtonText={formatMessage({ id: 'GENERAL.CANCEL_BUTTON' })}
      onClickPrimaryButton={handleMoveStores}
      onClickSecondaryButton={closeModal}
      showLink={false}
      showSlot={false}
      showTertiaryButton={false}
      showSecondaryButton
      text={`"${tierName}"`}
      title={formatMessage({ id: 'CLUB_PLUS.TIERS_MOVE_STORES_TITLE' })}
      type="S"
      key="move-stores-modal"
      className="move-stores-modal"
      content={(
        <>
          {isLoadingTiers ? <TableSkeletonLoader rows={3} /> : (
            <DataTable
              key={selectedTierID}
              columns={columns}
              data={tiersList}
              isDark
              selection={SELECTION_TYPE.SINGLE}
              showFilterButton={false}
              showColumnFilters={false}
              manualPagination={false}
              size={SIZE.LARGE}
              rowSelection={{
                tierName: tierName,
                tierID: tierID
              }}
              setRowSelection={(values) => {
                const selectionTierID = values[0]?.original?.tierID

                if (selectionTierID) {
                  setSelectedTierID(selectionTierID)
                }
              }}
            />
          )}
        </>
      )}
    />
  )
}